/**
 * Fetch All Categories API
 *
 * API function for fetching complete category tree with all categories and subcategories
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import type { CategoryTreeItem } from '../types';

export interface FetchAllCategoriesParams {
  includeInactive?: boolean;
}

export interface CategoryTreeResponse {
  tree: CategoryTreeItem[];
}

/**
 * Fetch complete category tree from the API
 *
 * @param params - Optional parameters for including inactive categories
 * @returns Promise with category tree structure
 */
export async function fetchAllCategories(
  params: FetchAllCategoriesParams = {},
): Promise<CategoryTreeItem[]> {
  const { includeInactive = false } = params;

  const url = buildApiUrl(
    `/categories/tree`,
  );

  console.log('[fetchAllCategories] Request URL:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  console.log('[fetchAllCategories] Response status:', response.status);

  const data = await handleApiResponse<CategoryTreeResponse>(response);
  

  // Check if data is already an array (not wrapped in {tree: []})
  if (Array.isArray(data)) {
    return data;
  }

  // Check if data has tree property
  if (data?.tree && Array.isArray(data.tree)) {
    return data.tree;
  }

  return [];
}

/**
 * Flatten category tree to get all categories as a flat array
 */
export function flattenCategoryTree(tree: CategoryTreeItem[]): CategoryTreeItem[] {
  const result: CategoryTreeItem[] = [];

  function traverse(items: CategoryTreeItem[]) {
    for (const item of items) {
      result.push(item);
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    }
  }

  traverse(tree);
  return result;
}

/**
 * Get popular categories from tree (categories marked as isPopular)
 */
export function getPopularFromTree(
  tree: CategoryTreeItem[],
  limit = 4,
): CategoryTreeItem[] {
  console.log('[getPopularFromTree] Input tree:', tree);
  console.log('[getPopularFromTree] Tree length:', tree.length);

  const allCategories = flattenCategoryTree(tree);
  console.log('[getPopularFromTree] Flattened categories:', allCategories.length);

  // Filter popular and root categories only
  const popularCategories = allCategories
    .filter((cat) => {
      const isPopular = cat.isPopular;
      const isRoot = !cat.parentId;
      console.log('[getPopularFromTree] Category:', cat.nameEn || cat.slug, '| isPopular:', isPopular, '| isRoot:', isRoot);
      return isPopular && isRoot;
    })
    .slice(0, limit);

  console.log('[getPopularFromTree] Popular categories found:', popularCategories.length);
  console.log('[getPopularFromTree] Result:', popularCategories);

  return popularCategories;
}

/**
 * Get subcategories for a specific category ID
 */
export function getSubcategoriesById(
  tree: CategoryTreeItem[],
  categoryId: string,
): CategoryTreeItem[] {
  const allCategories = flattenCategoryTree(tree);

  const category = allCategories.find((cat) => cat.id === categoryId);

  return category?.children ?? [];
}

/**
 * Get top categories sorted by the number of popular subcategories
 *
 * This function:
 * 1. Filters only root categories (no parentId)
 * 2. Counts popular subcategories for each root category
 * 3. Sorts by popular subcategory count (descending)
 * 4. Returns top N categories
 *
 * @param tree - Complete category tree
 * @param limit - Number of top categories to return (default: 4)
 * @returns Array of top categories with most popular subcategories
 */
export function getTopCategoriesByPopularSubcategories(
  tree: CategoryTreeItem[],
  limit = 4,
): CategoryTreeItem[] {
  console.log('[getTopCategoriesByPopularSubcategories] Input tree:', tree);
  console.log('[getTopCategoriesByPopularSubcategories] Tree length:', tree.length);

  // Filter only root categories (level 0, no parent)
  const rootCategories = tree.filter((cat) => !cat.parentId || cat.level === 0);

  console.log('[getTopCategoriesByPopularSubcategories] Root categories found:', rootCategories.length);

  // Map root categories with popular subcategory count
  const categoriesWithPopularCount = rootCategories.map((category) => {
    // Count popular subcategories (direct children with isPopular = true)
    const popularSubcategoriesCount = (category.children || []).filter(
      (child) => child.isPopular === true
    ).length;

    console.log(
      `[getTopCategoriesByPopularSubcategories] Category: ${category.nameEn || category.slug}`,
      `| Popular subcategories: ${popularSubcategoriesCount}`,
      `| Total children: ${category.children?.length || 0}`
    );

    return {
      category,
      popularSubcategoriesCount,
    };
  });

  // Sort by popular subcategories count (descending)
  const sortedCategories = categoriesWithPopularCount
    .sort((a, b) => b.popularSubcategoriesCount - a.popularSubcategoriesCount)
    .slice(0, limit)
    .map(({ category }) => category);

  console.log(
    '[getTopCategoriesByPopularSubcategories] Top categories:',
    sortedCategories.map((cat) => ({
      name: cat.nameEn || cat.slug,
      popularSubcategories: (cat.children || []).filter(c => c.isPopular).length,
    }))
  );

  return sortedCategories;
}

/**
 * Find category by name or slug
 *
 * @param tree - Complete category tree
 * @param searchTerm - Category name (nameEn) or slug
 * @returns Found category or undefined
 */
export function findCategoryByName(
  tree: CategoryTreeItem[],
  searchTerm: string,
): CategoryTreeItem | undefined {
  const allCategories = flattenCategoryTree(tree);
  const normalized = searchTerm.toLowerCase().trim();

  return allCategories.find(
    (cat) =>
      cat.nameEn?.toLowerCase() === normalized ||
      cat.nameFr?.toLowerCase() === normalized ||
      cat.slug?.toLowerCase() === normalized,
  );
}

/**
 * Find subcategory within a parent category
 *
 * @param tree - Complete category tree
 * @param parentName - Parent category name or slug
 * @param subcategoryName - Subcategory name or slug
 * @returns Found subcategory or undefined
 */
export function findSubcategoryByName(
  tree: CategoryTreeItem[],
  parentName: string,
  subcategoryName: string,
): CategoryTreeItem | undefined {
  const parent = findCategoryByName(tree, parentName);
  if (!parent || !parent.children) {
    return undefined;
  }

  const normalized = subcategoryName.toLowerCase().trim();

  return parent.children.find(
    (cat) =>
      cat.nameEn?.toLowerCase() === normalized ||
      cat.nameFr?.toLowerCase() === normalized ||
      cat.slug?.toLowerCase() === normalized,
  );
}
